import React, { useEffect, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { getAPIBaseURL } from "../services/helpers"
import { getData } from "../services/request"
import { statusMeta } from "../employme/JobApplicationsPage"

const statusFilters = [
  { key: "ALL", label: "All" },
  { key: "INTERVIEW_IN_PROGRESS", label: "Interview in progress" },
  { key: "SUBMITTED", label: "Submitted" },
  { key: "UNDER_REVIEW", label: "Under review" },
  { key: "SELECTED", label: "Selected" },
  { key: "REJECTED", label: "Rejected" },
  { key: "EXPIRED", label: "Expired" }
]

const EmployerApplicantsList = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [applications, setApplications] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [status, setStatus] = useState(searchParams.get("status") || "ALL")
  const [jobs, setJobs] = useState([])
  const [jobsLoading, setJobsLoading] = useState(true)
  const [selectedJobId, setSelectedJobId] = useState(searchParams.get("jobId") || "ALL")
  const [aiLoading, setAiLoading] = useState(false)
  const [aiMsg, setAiMsg] = useState("")

  const fetchApplications = async () => {
    setLoading(true)
    setError("")
    const url = `${getAPIBaseURL()}/employme/employer/applicants`
    const headers = {}
    const params = {}
    if (status && status !== "ALL") {
      params.status = status
    }
    if (selectedJobId && selectedJobId !== "ALL") {
      params.jobId = selectedJobId
    }
    const res = await getData(url, headers, params)
    setLoading(false)
    if (res.error === "") {
      if (res.result.status === 200) {
        setApplications(res.result.data?.data || [])
      } else {
        setError(res.result.data?.message || "Could not load applicants")
      }
    } else {
      setError(res.result?.data?.message || "Network error")
    }
  }

  const handleShortlistWithAI = async () => {
    if (!selectedJobId || selectedJobId === "ALL") {
      setError("Select a specific job before requesting AI shortlist.")
      return
    }
    setAiMsg("")
    setError("")
    setAiLoading(true)
    try {
      const url = `${getAPIBaseURL()}/employme/employer/jobs/${selectedJobId}/ai-shortlist`
      const axiosModule = await import("axios")
      const axios = axiosModule.default || axiosModule
      const res = await axios.post(
        url,
        { maxCandidates: 10 },
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true
        }
      )
      if (res.status === 200) {
        const shortlist = res.data?.data?.shortlist || []
        const ids = new Set(shortlist.map((s) => String(s.applicationId)))
        const withAi = applications.map((app) => ({
          ...app,
          _aiShortlisted: ids.has(String(app._id))
        }))
        setApplications(withAi)
        setAiMsg(
          shortlist.length
            ? "AI highlighted recommended candidates for this job."
            : "AI did not find clear recommendations; review candidates manually."
        )
      } else {
        setError(res.data?.data || res.data?.message || "Could not fetch AI shortlist")
      }
    } catch (e) {
      setError("Network error while requesting AI shortlist")
    } finally {
      setAiLoading(false)
    }
  }

  useEffect(() => {
    fetchApplications()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, selectedJobId])

  useEffect(() => {
    const fetchJobs = async () => {
      setJobsLoading(true)
      const url = `${getAPIBaseURL()}/employme/employer/jobs`
      const res = await getData(url, {}, {})
      setJobsLoading(false)
      if (res.error === "") {
        if (res.result.status === 200) {
          setJobs(res.result.data?.data || [])
        }
      }
    }

    fetchJobs()
  }, [])

  const setFilter = (value) => {
    setStatus(value)
    const next = new URLSearchParams(searchParams)
    if (value && value !== "ALL") {
      next.set("status", value)
    } else {
      next.delete("status")
    }
    setSearchParams(next)
  }

  const setJobFilter = (value) => {
    setSelectedJobId(value)
    const next = new URLSearchParams(searchParams)
    if (value && value !== "ALL") {
      next.set("jobId", value)
    } else {
      next.delete("jobId")
    }
    setSearchParams(next)
  }

  return (
    <div className="mx-auto max-w-6xl px-3 sm:px-0">
      <div className="mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 sm:text-xl">Applicants</h2>
          <p className="text-xs text-gray-500 sm:text-sm">
            Review candidates across your job posts, or focus on a specific job to see its applicants.
          </p>
        </div>
        <div className="grid w-full gap-2 sm:w-auto sm:grid-cols-3 sm:items-center">
          <div className="flex items-center gap-2">
            <span className="shrink-0 whitespace-nowrap text-xs font-medium text-gray-600">Status</span>
            <select
              value={status}
              onChange={(e) => setFilter(e.target.value)}
              className="w-full rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-700 focus:border-black focus:outline-none focus:ring-1 focus:ring-black sm:text-sm"
            >
              {statusFilters.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span className="shrink-0 whitespace-nowrap text-xs font-medium text-gray-600">Job</span>
            <select
              value={selectedJobId}
              onChange={(e) => setJobFilter(e.target.value)}
              className="w-full rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-700 focus:border-black focus:outline-none focus:ring-1 focus:ring-black sm:text-sm"
            >
              <option value="ALL">All jobs</option>
              {!jobsLoading &&
                jobs.map((job) => (
                  <option key={job._id} value={job._id}>
                    {job.title || "Untitled job"}
                  </option>
                ))}
            </select>
          </div>
          <button
            type="button"
            onClick={handleShortlistWithAI}
            disabled={aiLoading || !selectedJobId || selectedJobId === "ALL"}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-900 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
          >
            {aiLoading ? "Requesting AI shortlist..." : "AI shortlist"}
          </button>
        </div>
      </div>

      <div className="rounded-lg bg-white shadow-sm border border-gray-200">
        {loading ? (
          <div className="p-4 sm:p-6 text-sm text-gray-500">Loading...</div>
        ) : error ? (
          <div className="p-4 sm:p-6 text-sm text-red-600">{error}</div>
        ) : applications.length === 0 ? (
          <div className="p-4 sm:p-6 text-sm text-gray-500">
            No applicants found yet. Once candidates apply and complete interviews, they will appear here.
          </div>
        ) : (
          <>
            {aiMsg ? (
              <div className="px-4 pt-4 text-xs text-green-700 sm:px-6">{aiMsg}</div>
            ) : null}
            <ul className="divide-y divide-gray-200">
              {applications.map((app) => {
                const meta = statusMeta(app.status)
                const job = app.job || {}
                const candidate = app.candidate || {}
                const highlighted = app._aiShortlisted
                return (
                  <li
                    key={app._id}
                    className={`p-4 sm:p-5 ${highlighted ? "bg-yellow-50" : ""}`}
                  >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900">
                          {candidate.name || "Candidate"}
                        </span>
                        {candidate.email && (
                          <span className="text-xs text-gray-500 truncate max-w-xs">
                            {candidate.email}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-600">
                        Applied for:{" "}
                        <span className="font-medium text-gray-900">
                          {job.title || "Job"}
                        </span>
                      </p>
                      <div className="flex flex-wrap items-center gap-2 text-xs mt-1">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-1 font-medium ${meta.color}`}
                        >
                          {meta.label}
                        </span>
                        <span className="text-gray-400">
                          Submitted{" "}
                          {new Date(app.createdAt).toLocaleDateString(undefined, {
                            year: "numeric",
                            month: "short",
                            day: "numeric"
                          })}
                        </span>
                        {typeof app.matchingScore === "number" && (
                          <span className="text-gray-500">
                            Matching: <span className="font-semibold">{app.matchingScore}</span>
                          </span>
                        )}
                        {typeof app.interviewScore === "number" && (
                          <span className="text-gray-500">
                            Interview: <span className="font-semibold">{app.interviewScore}</span>
                          </span>
                        )}
                        {highlighted && (
                          <span className="inline-flex items-center rounded-full bg-yellow-200 px-2 py-0.5 text-[10px] font-semibold text-yellow-900">
                            AI shortlist
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex w-full flex-wrap gap-2 sm:w-auto sm:flex-nowrap sm:shrink-0">
                      <Link
                        to={`/employer/jobs/${job._id}/applicants/${app._id}`}
                        className="w-full sm:w-auto rounded-lg border border-gray-200 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 text-center"
                      >
                        View details
                      </Link>
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
          </>
        )}
      </div>
    </div>
  )
}

export default EmployerApplicantsList

